import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import BasePageUAM from '../BasePageUAM/BasePageUAM';
import { PrsButtonLg, PrsOutlineButtonLg, PrsButtonStackLg } from '../../styles-and-themes/styles/prsButtons';
import {
  UamHeading,
  UamBodyText,
  UamFlexColumn,
  UamFormSection,
  UamPageIcon,
} from '../../styles-and-themes/styles/uam-registration-styles';
import UamBanner from '../../components/PrsAlert/UamBanner';
import {
  PrsInput,
  PrsFormControl,
  PrsLabel,
  PrsLabelText,
} from '../../styles-and-themes/styles/prsInputs';
import { composeDestinationQuerystring } from '../../../shared-with-server/destination-utils';
import StepTransitionOverlay from '../../components/StepTransitionOverlay/StepTransitionOverlay';
import PrsResetGlobalStyle from '../../styles-and-themes/styles/prsReset';
import SecurityQuestionPanel from '../../shared-panels/uam/SecurityQuestionPanel/SecurityQuestionPanel';
import { destinationInfo, frontEndUrl } from '../../destructured-front-end-config';
import { SUPPORT_URL } from '../../constants';

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// States
const STEP_EMAIL = 'email';
const STEP_SECURITY_QUESTION = 'securityQuestion';
const STEP_DONE = 'done';
const STEP_LOCKED = 'lockedOut';

const LOCK_RESET_ICON_URL = `${frontEndUrl}static/images/lock_reset.svg`;
const FORWARD_TO_INBOX_ICON_URL = `${frontEndUrl}static/images/forward_to_inbox.svg`;
const REPORT_PROBLEM_ICON_URL = `${frontEndUrl}static/images/report_problem.svg`;

const ForgotPasswordUAMPage = ({ brand, destPath }) => {
  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [verificationData, setVerificationData] = useState(null); // { questionText, totalQuestions, questionIx }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [emailConfirmed, setEmailConfirmed] = useState(false); // true when we actually sent the email (vs silent done)

  const loginUrl = `/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`;

  const handleFlowResponse = ({
    ok, state, data, errors,
  }) => {
    if (ok && state === 'SECURITY_QUESTION') {
      setVerificationData({
        questionText: data.questionText,
        totalQuestions: data.totalQuestions || 1,
        questionIx: 0,
      });
      setStep(STEP_SECURITY_QUESTION);
      return;
    }

    if (ok && state === 'EMAIL_AUTH_PENDING') {
      setEmailConfirmed(true);
      setStep(STEP_DONE);
      return;
    }

    if (ok && state === 'LOCKED_OUT') {
      setStep(STEP_LOCKED);
      return;
    }

    if (!ok && state === 'SECURITY_QUESTION') {
      setVerificationData((prev) => ({
        ...prev,
        questionIx: (prev?.questionIx ?? -1) + 1,
        questionText: data?.questionText || prev?.questionText,
      }));
      setErrorMessage(errors?.[0] || 'Your answer was not correct.');
      return;
    }

    // Any other outcome (unknown email, no questions, unexpected state) — silently go to done
    setStep(STEP_DONE);
  };

  const onEmailSubmit = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!isValidEmail(email) || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const res = await axios.post('/api/v2/fi/flows/password-reset/start', {
        action: 'SUBMIT_EMAIL',
        payload: {
          email: email.toLowerCase().trim(),
          dest: destPath?.dest,
          path: destPath?.path,
          retURL: destPath?.retURL,
        },
      });
      handleFlowResponse(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status >= 500) {
        setErrorMessage('Something went wrong. Please try again.');
      } else {
        setStep(STEP_DONE);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onAnswerSubmit = async (answerText) => {
    if (!answerText?.length || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const res = await axios.post('/api/v2/fi/flows/password-reset/next', {
        action: 'SUBMIT_ANSWER',
        payload: {
          answerText,
          questionIndex: verificationData.questionIx,
        },
      });
      handleFlowResponse(res.data);
    } catch (_err) {
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  let content;
  if (step === STEP_SECURITY_QUESTION) {
    content = (
      <SecurityQuestionPanel
        questionText={verificationData.questionText}
        onSubmit={onAnswerSubmit}
        isSubmitting={isSubmitting}
        errorMessage={errorMessage}
        onBack={() => { window.location.href = loginUrl; }}
      />
    );
  } else if (step === STEP_DONE && emailConfirmed) {
    content = (
      <UamFlexColumn gap="16px">
        <UamPageIcon src={FORWARD_TO_INBOX_ICON_URL} alt="" />
        <UamHeading>Reset Instructions Sent!</UamHeading>
        <UamBodyText id="fp-sent-email">
          {"We've sent an email to "}
          <strong style={{ wordBreak: 'break-all' }}>{email}</strong>
          . Follow the instructions in the email to reset your password.
        </UamBodyText>
      </UamFlexColumn>
    );
  } else if (step === STEP_DONE) {
    content = (
      <UamFlexColumn gap="16px">
        <UamPageIcon src={FORWARD_TO_INBOX_ICON_URL} alt="" />
        <UamHeading>Check your email.</UamHeading>
        <UamFlexColumn gap="24px">
          <UamBodyText>
            {'If '}
            <strong style={{ wordBreak: 'break-all' }}>{email}</strong>
            {" is associated with an account, you'll receive an email with a link to reset your password."}
          </UamBodyText>
          <PrsButtonStackLg>
            <PrsOutlineButtonLg
              type="button"
              style={{ width: '100%' }}
              onClick={() => { window.location.href = loginUrl; }}
            >
              Back to Sign In
            </PrsOutlineButtonLg>
          </PrsButtonStackLg>
        </UamFlexColumn>
      </UamFlexColumn>
    );
  } else if (step === STEP_LOCKED) {
    content = (
      <UamFlexColumn gap="16px">
        <UamPageIcon src={REPORT_PROBLEM_ICON_URL} alt="" />
        <UamHeading>We couldn&apos;t verify your identity.</UamHeading>
        <UamBodyText>
          You&apos;ve reached the maximum number of verification attempts. Please try again later.
        </UamBodyText>
        <UamBodyText>
          {'If the issue continues, '}
          <a href={SUPPORT_URL} style={{ color: '#0600b4', textDecoration: 'underline' }}>contact support</a>
          {' for help verifying your account.'}
        </UamBodyText>
        <PrsButtonStackLg>
          <PrsButtonLg
            type="button"
            style={{ width: '100%' }}
            onClick={() => { window.location.href = loginUrl; }}
          >
            Back to Sign In
          </PrsButtonLg>
        </PrsButtonStackLg>
      </UamFlexColumn>
    );
  } else {
    content = (
      <StepTransitionOverlay loading={isSubmitting}>
        <UamFlexColumn gap="0">
          {errorMessage && (
            <UamBanner variant="danger">{errorMessage}</UamBanner>
          )}
          <UamFlexColumn gap="16px" padding={errorMessage ? '24px 0 0' : '0'}>
            <UamPageIcon src={LOCK_RESET_ICON_URL} alt="" />
            <UamHeading>Password Reset</UamHeading>
            <form onSubmit={onEmailSubmit}>
              <UamFormSection>
                <UamBodyText>
                  Let&apos;s start with entering the email address you used to create your account.
                </UamBodyText>
                <PrsFormControl $error={!!errorMessage}>
                  <PrsLabel>
                    <PrsLabelText>Email</PrsLabelText>
                  </PrsLabel>
                  <PrsInput
                    id="reset-email"
                    type="email"
                    placeholder="user@domain.ext"
                    value={email}
                    autoComplete="email"
                    onChange={(ev) => setEmail(ev.target.value)}
                    $error={!!errorMessage}
                  />
                </PrsFormControl>
              </UamFormSection>
              <PrsButtonStackLg>
                <PrsButtonLg
                  type="submit"
                  style={{ width: '100%' }}
                  disabled={!isValidEmail(email) || isSubmitting}
                >
                  Continue
                </PrsButtonLg>
                <PrsOutlineButtonLg
                  type="button"
                  style={{ width: '100%' }}
                  onClick={() => { window.location.href = loginUrl; }}
                >
                  Back to Sign In
                </PrsOutlineButtonLg>
              </PrsButtonStackLg>
            </form>
          </UamFlexColumn>
        </UamFlexColumn>
      </StepTransitionOverlay>
    );
  }

  return (
    <>
      {/* Page-scoped reset; lift to App.js once verified safe across all pages. */}
      <PrsResetGlobalStyle />
      <BasePageUAM
        brand={brand}
        destPath={destPath}
        needsDestPath
        showTabs={false}
      >
        {content}
      </BasePageUAM>
    </>
  );
};

ForgotPasswordUAMPage.propTypes = {
  brand: PropTypes.object.isRequired,
  destPath: PropTypes.object.isRequired,
};

export default ForgotPasswordUAMPage;
